import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {RichText} from '@payloadcms/richtext-lexical/react';

/**
 * Props for the TextBlock component.
 */
interface ComponentProps {
    /** The Lexical rich text content to render. */
    content: any;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     *
     * @default 'TextBlock'
     */
    testId?: string;
}

/**
 * Renders a rich text block from the Lexical editor content stored in the Homepage global.
 *
 * @param props         The component props.
 * @param props.content The Lexical serialized rich text data.
 * @param props.testId  An optional test ID. Defaults to `'TextBlock'`.
 * @returns A JSX element rendering the rich text content.
 *
 * @example
 * ```tsx
 * <TextBlock content={block.content} />
 * ```
 */
const TextBlock = ({content, testId = 'TextBlock'}: ComponentProps) => {
    if (!content) return null;

    return (
        <Wrapper data-cy={testId}>
            <RichText data={content} />
        </Wrapper>
    );
};

TextBlock.displayName = 'TextBlock';

export {TextBlock};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.section`
    color: ${({theme}) => theme.colors.inkSecondary};
    max-width: 78rem;
    padding: ${spacing(2)} 0;

    p {
        font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
        font-size: 1.6rem;
        line-height: 1.6;
        margin: 0 0 ${spacing(4)};
    }

    h2,
    h3,
    h4 {
        color: ${({theme}) => theme.colors.inkPrimary};
        font-family: ${({theme}) => theme.fonts.NotoSerif};
        line-height: 1.2;
        margin: ${spacing(6)} 0 ${spacing(3)};
    }

    h2 {
        font-size: 2.8rem;
    }

    h3 {
        font-size: 2.2rem;
    }

    h4 {
        font-size: 1.8rem;
    }

    a {
        color: ${({theme}) => theme.colors.primaryDefault};
        font-weight: 700;
        text-decoration: underline;
    }
`;